import React, { useCallback, useEffect, useState } from "react";
import { Card, Select, Table, Tag } from "antd";
import moment from "moment";
import useRequest from "../hooks/useRequest";
import apiPath from "../constants/apiPath";
import { Severty, ShowToast } from "../helper/toast";

const actionColors = {
  create: "green",
  update: "blue",
  delete: "red",
  activate: "green",
  deactivate: "orange",
  price_changed: "gold",
  trial_days_changed: "purple",
  status_changed: "cyan",
};

const AuditLogs = () => {
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [action, setAction] = useState("all");

  const fetchLogs = useCallback(async (nextAction = action) => {
    setLoading(true);
    try {
      const query = nextAction !== "all" ? `?module=plan&action=${nextAction}` : "?module=plan";
      await request({
        url: `${apiPath.adminAuditLogs}${query}`,
        method: "GET",
        onSuccess: (response) => {
          setLogs(Array.isArray(response?.data) ? response.data : []);
        },
        onError: (error) => {
          ShowToast(
            error?.response?.data?.message || error?.message || "Failed to fetch audit logs",
            Severty.ERROR,
          );
        },
      });
    } finally {
      setLoading(false);
    }
  }, [action, request]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: "Timestamp",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => moment(value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (value) => <Tag color={actionColors[value] || "default"}>{value}</Tag>,
    },
    {
      title: "Admin",
      dataIndex: "adminId",
      key: "adminId",
      render: (admin) => admin?.name || admin?.email || "System",
    },
    {
      title: "Entity",
      dataIndex: "afterData",
      key: "entity",
      render: (afterData, record) => afterData?.name || record?.beforeData?.name || "Plan",
    },
    {
      title: "Change Summary",
      key: "summary",
      render: (_, record) => {
        if (record.action === "price_changed") {
          return `Monthly price changed from £${Number((record.beforeData?.monthlyPrice || 0) / 100).toFixed(2)} to £${Number((record.afterData?.monthlyPrice || 0) / 100).toFixed(2)}`;
        }
        if (record.action === "trial_days_changed") {
          return `Trial days changed from ${record.beforeData?.trialDays || 0} to ${record.afterData?.trialDays || 0}`;
        }
        if (record.action === "status_changed" || record.action === "activate" || record.action === "deactivate") {
          return `Status changed from ${record.beforeData?.isActive ? "active" : "inactive"} to ${record.afterData?.isActive ? "active" : "inactive"}`;
        }
        return "Plan details updated";
      },
    },
  ];

  return (
    <Card
      bordered={false}
      className="criclebox"
      title="Audit Logs"
      extra={
        <Select
          style={{ width: 220 }}
          value={action}
          onChange={(value) => {
            setAction(value);
            fetchLogs(value);
          }}
          options={[
            { value: "all", label: "All plan actions" },
            { value: "create", label: "Created" },
            { value: "update", label: "Edited" },
            { value: "price_changed", label: "Price changed" },
            { value: "trial_days_changed", label: "Trial days changed" },
            { value: "status_changed", label: "Status changed" },
            { value: "delete", label: "Deleted" },
          ]}
        />
      }
    >
      <Table
        rowKey={(record) => record._id}
        loading={loading}
        columns={columns}
        dataSource={logs}
        scroll={{ x: 1100 }}
      />
    </Card>
  );
};

export default AuditLogs;
